import CameraService, { CameraPermissionState } from '../../../helpers/classes/CameraService';
import EventService from '../../../helpers/classes/EventService';
import { ExceptionManager } from '../../../helpers/classes/ExceptionManager';
import LanguageService from '../../../helpers/classes/LanguageService';
import RenderingService, { ISelectItem } from '../../../helpers/classes/RenderingService';
import SocketService from '../../../helpers/classes/SocketService';
import TemplateService from '../../../helpers/classes/TemplateService';
import { TemplateType } from '../../../helpers/enums/Templates';
import { JourneyContainer } from '../../classes/JourneyContainer';
import JourneyManager from '../../classes/JourneyManager';
import { ChangeMode } from '../../enums/InputProvider';
import { JourneyEvent } from '../../enums/JourneyEvent';
import { JourneyStep } from '../../enums/JourneyStep';
import { Templates } from '../../enums/Templates';
import { TranslationKey } from '../../enums/Translation';
import { IJourneyDefinition } from '../../interfaces/JourneyDefinition';
import { STORAGE_KEYS } from '../../configuration/StorageKeys';
import StorageService from '../../../helpers/classes/StorageService';


export class JourneySelectStep {

	public static journeyDefinitionsCount = 0;

	public static render(this: JourneyContainer) {
		return TemplateService.renderToContainer(this._container, Templates.Initializing)
			.then(() => JourneyManager.getJourneyDefinitions())
			.then((journeyDefinitions = []) => {
				JourneySelectStep.journeyDefinitionsCount = journeyDefinitions.length;
				const externalJourneyDefinition = journeyDefinitions.find((d) => d.JourneyDefinitionId === this._config.journeyDefinitionId);
				const externalHeaderJourneyDefinition = journeyDefinitions.find((d) => d.JourneyDefinitionId === StorageService.get(STORAGE_KEYS.journeyDefinitionId)!);
				if (externalJourneyDefinition) {
					JourneySelectStep.chooseJourney.call(this, externalJourneyDefinition);
				}
				else if (externalHeaderJourneyDefinition) {
					JourneySelectStep.chooseJourney.call(this, externalHeaderJourneyDefinition);
				}
				else {
					TemplateService.renderToContainer(this._container, Templates.JourneySelect)
						.then(() => {
							const data = {
								definitions: journeyDefinitions.map((definition) => ({
									_definitionName: definition.Name,
									entryDefinitions: definition.EntryDefinitions.map((entry) => ({
										_type: JourneySelectStep.getJourneyEntryDefinitionName(entry.Type),
										_id: entry.JourneyEntryDefinitionId
									}))
								}))
							};

							return TemplateService.renderToContainer(this._container, Templates.JourneySelect, data);
						})
						.then((element) => TemplateService.getElements(element, {
							choices: '[data-jcs-element="journey-select__choices"]',
							select: '[data-jcs-element="journey-select__continue"]'
						}))
						.then(({ choices, select }) => {
							if (
								choices instanceof HTMLSelectElement &&
								select instanceof HTMLInputElement
							) {
								if (journeyDefinitions.length === 0) {
									RenderingService.updateSelect(choices, [{
										name: LanguageService.getTranslation(TranslationKey.EXCEPTION_JOURNEY_DEFINITIONS_MISSING),
										value: '',
										disabled: true
									}]);
									RenderingService.setEnabled(select, false);

									throw ExceptionManager.process(new ExceptionManager.TemplateException(TranslationKey.EXCEPTION_JOURNEY_DEFINITIONS_MISSING));
								} else if (journeyDefinitions.length === 1) {
									JourneySelectStep.chooseJourney.call(this, journeyDefinitions[0]);
								} else {
									select.addEventListener('click', (e) => JourneySelectStep.chooseJourney.call(this, journeyDefinitions.find((definition) => definition.JourneyDefinitionId === choices.value)));

									RenderingService.updateSelect(choices, journeyDefinitions.reduce((out, curr, i) => [
										...out,
										{
											name: curr.Name,
											value: curr.JourneyDefinitionId,
											selected: i === 0
										}
									], [] as ISelectItem[]));
								}
							} else {
								throw ExceptionManager.process(new ExceptionManager.TemplateException(TranslationKey.EXCEPTION_TEMPLATE_ELEMENTS_MISSING));
							}
						})
				}
			})
			.then(() => EventService.publish(JourneyEvent.DISPLAY_STEP, { step: Templates.JourneySelect }));
	}

	private static chooseJourney(this: JourneyContainer, journeyDefinition?: IJourneyDefinition): void {
		if (journeyDefinition) {

			let hasLivenessStep = false;
			journeyDefinition.EntryDefinitions.forEach(element => {
				if (JourneyStep.LIVENESS === JourneySelectStep.getJourneyEntryDefinitionName(element.Type)) {
					return hasLivenessStep = true;
				}
			});

			if (hasLivenessStep) {
				// There is Liveness Step
				CameraService.getVideoCameras()
					.then(devices => {
						if (devices.length === 0) {
							TemplateService.renderToContainer(this._container, Templates.HasNoCamera)
								.then(() => {
									if (JourneySelectStep.journeyDefinitionsCount === 1) {
										this.hookGlobalButtons(false);
									}
								});
						}
						else { //There is camera
							if (devices.length != 0) {
								CameraService.getVideoCameras()
								Promise.all([
									CameraService.getCameraPermission(),
									CameraService.hasDeviceCapableOfResolution()
								]).then(([permission]) => {
									switch (permission) {
										case CameraPermissionState.DENIED:
											return TemplateService.renderToContainer(this._container, Templates.HasNoCamera)
												.then(() => {
													if (JourneySelectStep.journeyDefinitionsCount === 1) {
														this.hookGlobalButtons(false);
													}
												});
											break
										case CameraPermissionState.GRANTED:
											this._journeyDefinitionId = journeyDefinition.JourneyDefinitionId;
											TemplateService.renderToContainer(this._container, Templates.Initializing)
												.then(() => this.setProviders(journeyDefinition.CapturingMedia, journeyDefinition.IsPredefinedJourney))
												.then(() => this.changeInputProvider(this._config.defaultInputProvider, ChangeMode.FALLBACK))
												.then(() => this.evaluate(false, journeyDefinition.IsPredefinedJourney));
											break
										default:
											this._journeyDefinitionId = journeyDefinition.JourneyDefinitionId;
											TemplateService.renderToContainer(this._container, Templates.Initializing)
												.then(() => this.setProviders(journeyDefinition.CapturingMedia, journeyDefinition.IsPredefinedJourney))
												.then(() => this.changeInputProvider(this._config.defaultInputProvider, ChangeMode.FALLBACK))
												.then(() => this.evaluate(false, journeyDefinition.IsPredefinedJourney));
											break;
									}
								});
							}
						}
					})
			}
			else { // There is no Liveness Step
				if (journeyDefinition.CapturingMedia === 1) {
					this.getJourney().then(result => {
						SocketService.start(journeyDefinition.JourneyDefinitionId, result.id)
							.catch(() => {
								/* NO-OP */
							})
							.then(() => SocketService.release())
					})
				}
				else {
					SocketService.stop();
				}
				this._journeyDefinitionId = journeyDefinition.JourneyDefinitionId;

				TemplateService.renderToContainer(this._container, Templates.Initializing)
					.then(() => this.setProviders(journeyDefinition.CapturingMedia, journeyDefinition.IsPredefinedJourney))
					.then(() => this.changeInputProvider(this._config.defaultInputProvider, ChangeMode.FALLBACK))
					.then(() => this.evaluate(false, journeyDefinition.IsPredefinedJourney));
			}
		}
	}




	private static getJourneyEntryDefinitionName(entryDefinitionType: number): any {
		switch (entryDefinitionType) {
			case 1: {
				return JourneyStep.FRONTSIDE;
				break;
			}
			case 2: {
				return JourneyStep.BACKSIDE;
				break;
			}
			case 3: {
				return JourneyStep.SELFIE;
				break;
			}
			case 4: {
				return JourneyStep.ADDRESSDOCUMENT;
				break;
			}
			case 6: {
				return JourneyStep.LIVENESS;
				break;
			}
			case 9: {
				return JourneyStep.COVIDPASS;
				break;
			}
			case 10: {
				return JourneyStep.DATACHECKS;
				break;
			}
			default: {
				break;
			}
		}
	}
}
